import type { CoinbaseOnrampOrderPaymentMethod } from '@dynamic-labs/sdk-api-core';

import { getCore } from '../../../../client/core/getCore';
import { getDefaultClient } from '../../../../client/defaultClient';
import { createApiClient } from '../../../apiClient';
import type {
  CoinbaseCreateOnrampOrderRequest,
  CoinbaseOnrampOrderResponse,
} from '../coinbase.types';
import { validateUserCredentialsForCoinbaseOnrampOrder } from '../utils/validateUserCredentialsForCoinbaseOnrampOrder';

/**
 * Creates a Coinbase onramp order
 *
 * @param orderParams CoinbaseCreateOnrampOrderRequest - The parameters for the onramp order.
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns The created order
 * @throws {MissingUserVerificationError} If the user is missing verification for email or phone
 */
export const createCoinbaseOnrampOrder = async (
  orderParams: CoinbaseCreateOnrampOrderRequest,
  client = getDefaultClient()
): Promise<CoinbaseOnrampOrderResponse> => {
  const user = validateUserCredentialsForCoinbaseOnrampOrder(client);

  const core = getCore(client);
  const apiClient = createApiClient({ includeMfaToken: true }, client);

  const { isSandbox, ...restOrderParams } = orderParams;

  const orderResponse = await apiClient.createCoinbaseOnrampOrder({
    coinbaseOnrampOrderCreateRequest: {
      ...restOrderParams,
      // to create a sandbox order, we need to prefix the partner user ref with 'sandbox-'
      // https://docs.cdp.coinbase.com/onramp-&-offramp/onramp-apis/apple-pay-onramp-api#testing
      partnerUserRef:
        orderParams.partnerUserRef ??
        (isSandbox ? `sandbox-${user.id}` : user.id),
      paymentMethod:
        orderParams.paymentMethod as CoinbaseOnrampOrderPaymentMethod,
    },
    environmentId: core.environmentId,
  });

  if (!isSandbox || !orderResponse.paymentLink) {
    return orderResponse;
  }

  return {
    ...orderResponse,
    paymentLink: {
      ...orderResponse.paymentLink,
      url: `${orderResponse.paymentLink.url}&useApplePaySandbox=true`,
    },
  };
};
